import { useCallback, useMemo } from 'react';
import { type DefaultOptions, defaultShouldDehydrateQuery, environmentManager, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types/generated/api';
import { APIError } from './api-error';
import { MutationNormalizationCache } from './query-client/mutation-cache';
import { NormalizationEntityCache } from './query-client/normalization-entity-cache';
import { QueryNormalizationCache } from './query-client/query-cache';
import { createRealmAuthStoreHooks, defaultContextChangeActiveAccessSelector, realmAccessIdGetter, type WhoAmIContext } from './realm-auth/store';
import { TenantContext } from './realm-context';

const ACTIVE_REALM_ACCESS_LOCAL_STORAGE_KEY = '@@ix__realm_access_id@@';

export function getLocalStorageActiveRealmAccessId() {
  return localStorage.getItem(ACTIVE_REALM_ACCESS_LOCAL_STORAGE_KEY);
}

export function setLocalStorageActiveRealmAccessId(realmAccessId: string) {
  localStorage.setItem(ACTIVE_REALM_ACCESS_LOCAL_STORAGE_KEY, realmAccessId);
}

export function deleteLocalStorageActiveRealmAccessId() {
  localStorage.removeItem(ACTIVE_REALM_ACCESS_LOCAL_STORAGE_KEY);
}

export function contextChangeActiveAccessSelector(context: WhoAmIContext | undefined, activeAccess: IxmUserauthV1JoinedRealmAccess | undefined) {
  if (context && activeAccess) {
    const access = context.accesses.find((ctxAccess) => realmAccessIdGetter(ctxAccess) === realmAccessIdGetter(activeAccess));

    if (access) {
      return access;
    }
  }

  const fromLocalStorage = getLocalStorageActiveRealmAccessId();

  if (context?.accesses && fromLocalStorage) {
    for (const access of context.accesses) {
      if (access.access?.realmAccessId && access.access.realmAccessId === fromLocalStorage) {
        return access;
      }
    }

    deleteLocalStorageActiveRealmAccessId();
  }

  if (context?.data?.preferences?.defaultRealmAccessId) {
    for (const access of context.accesses) {
      if (access.access?.realmAccessId && access.access.realmAccessId === context.data.preferences.defaultRealmAccessId) {
        return access;
      }
    }
  }

  const clearingAccess = context?.accesses.find((access) => access.tenant?.tenantType?.toLowerCase() === TenantContext.Clearing);

  if (clearingAccess) {
    return clearingAccess;
  }

  for (const access of context?.accesses || []) {
    if (access.tenant?.tenantType?.toLowerCase() === 'program') {
      return access;
    }
  }

  return defaultContextChangeActiveAccessSelector(context, activeAccess);
}

const {
  useRealmAuthStore,
  useRealmAuthActiveAccess,
  useRealmAuthContext,
  useRealmAuthIsAuthenticating,
  useRealmAuthIsAuthenticated,
  useRealmAuthReset,
  useRealmAuthSetActiveAccess,
  useRealmAuthSetContext,
  useRealmAuthSetIsAuthenticated,
  useRealmAuthSetIsAuthenticating,
  useRealmAuthActiveOrganizationMembership,
} = createRealmAuthStoreHooks({}, contextChangeActiveAccessSelector);

function useHandleRealmAccessChange() {
  const defaultHandleRealmAccessChange = useRealmAuthSetActiveAccess();

  return useCallback(
    (newRealmAccessId: string) => {
      setLocalStorageActiveRealmAccessId(newRealmAccessId);
      defaultHandleRealmAccessChange(newRealmAccessId);
    },
    [defaultHandleRealmAccessChange],
  );
}

// 10-minute default stale time
const defaultQueryClientOptions = {
  queries: {
    staleTime: 600_000,
    retry: function baseRetryHandler(count, error) {
      if (error instanceof APIError) {
        // Do not retry for 4xx errors
        if (error.status >= 400 && error.status < 500) {
          return false;
        }
      }

      return count < 3;
    },
  },
} as const satisfies DefaultOptions;

function makeQueryClient(ssrContext?: boolean) {
  const ec = new NormalizationEntityCache();

  return new QueryClient({
    defaultOptions: {
      ...defaultQueryClientOptions,
      dehydrate: ssrContext
        ? {
            shouldDehydrateQuery: (query) => defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
          }
        : undefined,
    },
    queryCache: new QueryNormalizationCache(ec),
    mutationCache: new MutationNormalizationCache(ec),
  });
}

export let queryClient: QueryClient = makeQueryClient();

export function getQueryClient(ssrContext?: boolean) {
  if (environmentManager.isServer()) {
    return makeQueryClient(ssrContext);
  } else {
    if (!queryClient) {
      queryClient = makeQueryClient();
    }

    return queryClient;
  }
}

export {
  QueryClientProvider,
  useHandleRealmAccessChange as useRealmAuthSetActiveAccess,
  useRealmAuthActiveAccess,
  useRealmAuthActiveOrganizationMembership,
  useRealmAuthContext,
  useRealmAuthIsAuthenticated,
  useRealmAuthIsAuthenticating,
  useRealmAuthReset,
  useRealmAuthSetContext,
  useRealmAuthSetIsAuthenticated,
  useRealmAuthSetIsAuthenticating,
  useRealmAuthStore,
};

export function useActiveProgramId(): string | undefined {
  const activeAccess = useRealmAuthActiveAccess();

  if (activeAccess?.tenant?.tenantType?.toLowerCase() === TenantContext.Program) {
    return activeAccess?.tenant.tenantId;
  }

  return undefined;
}

export function useActiveProgramOrganizationId(): string | undefined {
  const activeAccess = useRealmAuthActiveAccess();
  const activeProgramId = useActiveProgramId();

  if (activeProgramId && activeAccess?.tenant?.tenantId === activeProgramId) {
    return activeAccess.access?.organizationId;
  }

  return undefined;
}

export function useActiveOrganizationId(): string | undefined {
  const activeAccess = useRealmAuthActiveAccess();

  if (activeAccess?.tenant?.tenantType?.toLowerCase() === TenantContext.Organization) {
    return activeAccess?.tenant.tenantId;
  }

  return undefined;
}

export function useUserHasClearingTenantAccess() {
  const { accesses } = useRealmAuthContext() || {};
  return useMemo(() => Boolean(accesses?.some((access) => access.tenant?.tenantType?.toLowerCase() === TenantContext.Clearing)), [accesses]);
}

export function useUserHasTechOpsTenantAccess() {
  const { accesses } = useRealmAuthContext() || {};
  return useMemo(() => Boolean(accesses?.some((access) => access.tenant?.tenantType?.toLowerCase() === TenantContext.O5)), [accesses]);
}

export function useUserHasPlatformTenantAccess() {
  const { accesses } = useRealmAuthContext() || {};
  return useMemo(() => Boolean(accesses?.some((access) => access.tenant?.tenantType?.toLowerCase() === TenantContext.Platform)), [accesses]);
}
